import { useMemoizedFn } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import qs from 'qs';
import {
  Button,
  DialogPlugin,
  Form,
  Input,
  MessagePlugin,
  Radio,
  Row,
  Select,
  Tag,
  Textarea,
} from 'tdesign-react';
import formRulePattern from '@/utils/formRulePattern';
import { isVoid, toTsOptions } from '@/utils/common';
import { clusterList, createCluster, getClusterDetail } from '@/services/resourceService';
import Tip from '@/components/Tip';
import Text from '@/components/Text';
import TextSelect from '@/components/TextSelect';

const { FormItem } = Form;

function ChoiceClusterStepK8s({ clusterType, cache, quickDeployFlow, stencilOptions }) {
  const [form] = Form.useForm();
  const [clusterOptions, setClusterOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const selectCluster = Form.useWatch('selectCluster', form);
  const opsEnvId = Form.useWatch('opsEnvId', form);

  const resetClusterInfo = () => {
    form.reset({
      fields: [
        'cluster',
        'name',
        'key',
        'clusterInstallPath',
        'gvarHostInstallUser',
        'desc',
        'namespace',
        'contexts',
        'varStencilId',
      ],
    });
  };

  const envChange = useMemoizedFn(async (id) => {
    resetClusterInfo();
    form.setFields([{ name: 'selectCluster', value: 'choiceExists' }]);
    const { data } = await clusterList({
      envId: id,
      pageSize: 99999,
      startIndex: 1,
      type: clusterType,
    });
    setClusterOptions(
      data.items
        .filter((item) => item.k8sId === cache.k8sId)
        .map((item) => ({
          ...item,
          value: item.id,
          label: item.name,
        })),
    );
  });

  useEffect(() => {
    if (!isVoid(cache.opsEnvId)) {
      form.setFieldsValue({ opsEnvId: cache.opsEnvId });
      envChange(cache.opsEnvId);
    }
  }, [cache, envChange, form]);

  const clusterChange = async (id) => {
    const { data } = await getClusterDetail(id);
    form.setFieldsValue({
      name: data.name,
      key: data.key,
      clusterInstallPath: data.clusterInstallPath,
      gvarHostInstallUser: data.gvarHostInstallUser,
      desc: data.desc,
      namespace: data.namespace,
      contexts: cache.contexts,
      varStencilId: data.varStencilId,
    });
  };

  // 下一步
  const doNext = (params) => {
    const { k8s } = quickDeployFlow;
    if (!k8s) {
      MessagePlugin.warning('未找到执行流程');
      return;
    }
    navigate(
      `/maintenance/executive/flow/detail${qs.stringify(
        { ...params, flowId: k8s.id, flowType: clusterType },
        { addQueryPrefix: true },
      )}`,
    );
  };

  // 创建集群
  const doCreateCluser = async (params) => {
    setSubmitting(true);
    try {
      const { data } = await createCluster(params);
      return data;
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async ({ validateResult, fields }) => {
    if (validateResult === true) {
      const values = { ...cache, ...fields };
      if (selectCluster === 'createNew') {
        const { key } = values;

        // K8s必须-asp后缀
        if (key.indexOf('-asp') < 0) {
          const _key = `${key}-asp`;
          const dialog = DialogPlugin.confirm({
            header: '提示',
            theme: 'warning',
            body: (
              <>
                集群类型为K8S容器时，集群名称必须添加-asp后缀
                <br /> 自动将 <Tag>{key}</Tag> 替换为 <Tag>{_key}</Tag>
              </>
            ),
            confirmBtn: {
              content: '确定',
            },
            onClose: () => dialog.hide(),
            onConfirm: async () => {
              form.setFieldsValue({ key: _key });
              dialog.hide();
              const clusterId = await doCreateCluser({
                ...values,
                key: _key,
                type: clusterType,
              });
              doNext({
                envId: values.opsEnvId,
                clusterId,
              });
            },
          });
        } else {
          const clusterId = await doCreateCluser({
            ...values,
            type: clusterType,
          });
          doNext({
            envId: values.opsEnvId,
            clusterId,
          });
        }
        return;
      }
      doNext({
        envId: values.opsEnvId,
        clusterId: values.cluster,
      });
    }
  };

  const selectClusterChange = (value) => {
    resetClusterInfo();
    if (value === 'createNew') {
      setTimeout(() => {
        form.setFieldsValue({ clusterInstallPath: '/root', contexts: cache.contexts });
      }, 50);
    }
  };

  return (
    <Root>
      <Form
        form={form}
        labelWidth={selectCluster === 'createNew' ? 130 : 110}
        style={{ width: '80%' }}
        initialData={{ selectCluster: 'choiceExists' }}
        onSubmit={handleSubmit}
      >
        <FormItem style={{ display: 'none' }} name='opsEnvId' label='环境' />
        {!isVoid(opsEnvId) && (
          <>
            <FormItem
              name='selectCluster'
              label={
                <Tip content='集群表示需要创建的Pulsar集群，集群名称则为Plusar cluster名称，如果在下拉菜单中没有找到集群，您可新建一个集群'>
                  选择
                </Tip>
              }
            >
              <Radio.Group onChange={selectClusterChange}>
                <Radio value='choiceExists'>现有集群</Radio>
                <Radio value='createNew'>新建集群</Radio>
              </Radio.Group>
            </FormItem>
            {selectCluster === 'createNew' ? (
              <>
                <FormItem
                  name='name'
                  label={<Tip content='集群显示名称（可以用中文表示）'>集群显示名称</Tip>}
                  rules={[{ required: true, message: '集群显示名称必填' }]}
                >
                  <Input />
                </FormItem>

                <FormItem
                  rules={[{ required: true, message: '集群名称必填' }, formRulePattern('name')]}
                  name='key'
                  label={
                    <Tip
                      content={
                        <>
                          集群名称（内部唯一标识符）
                          <br /> 集群类型为K8S容器时，必须添加-asp后缀
                        </>
                      }
                    >
                      集群名称
                    </Tip>
                  }
                >
                  <Input />
                </FormItem>
                <FormItem
                  name='contexts'
                  label={
                    <Tip align='right' content='Operator所在Namespace，从选择的k8s自动获取'>
                      Namespace
                    </Tip>
                  }
                >
                  <Text />
                </FormItem>
                <FormItem
                  name='clusterInstallPath'
                  label={
                    <Tip
                      align='right'
                      content='kubectl执行机器的基础目录，该集群的基础部署路径，自动部署时，会以该路径为前缀'
                    >
                      基础目录
                    </Tip>
                  }
                >
                  <Text />
                </FormItem>
                <FormItem
                  name='namespace'
                  rules={[{ required: true }, formRulePattern('name'), { max: 50 }]}
                  label={
                    <Tip align='right' content='需要部署pulsar集群的Namespace'>
                      Namespace
                    </Tip>
                  }
                >
                  <Input />
                </FormItem>
                <FormItem name='varStencilId' label='变量模板' rules={[{ required: true }]}>
                  <Select options={toTsOptions(stencilOptions)} />
                </FormItem>
                <FormItem name='desc' label='集群说明' rules={[{ required: true }]}>
                  <Textarea placeholder='集群说明' />
                </FormItem>
              </>
            ) : (
              <>
                <FormItem name='cluster' label='选择集群' rules={[{ required: true }]}>
                  <Select options={toTsOptions(clusterOptions)} onChange={clusterChange} />
                </FormItem>
                <FormItem name='name' label='集群名称'>
                  <Text />
                </FormItem>
                <FormItem name='key' label='唯一标识'>
                  <Text />
                </FormItem>
                <FormItem
                  name='contexts'
                  label={
                    <Tip align='right' content='Operator所在Namespace，从选择的k8s自动获取'>
                      Namespace
                    </Tip>
                  }
                >
                  <Text />
                </FormItem>
                <FormItem
                  name='clusterInstallPath'
                  label={
                    <Tip
                      align='right'
                      content='kubectl执行机器的基础目录，该集群的基础部署路径，自动部署时，会以该路径为前缀'
                    >
                      基础目录
                    </Tip>
                  }
                >
                  <Text />
                </FormItem>
                <FormItem
                  name='namespace'
                  label={
                    <Tip align='right' content='需要部署pulsar集群的Namespace'>
                      Namespace
                    </Tip>
                  }
                >
                  <Text />
                </FormItem>
                <FormItem name='varStencilId' label='变量模板'>
                  <TextSelect options={stencilOptions} />
                </FormItem>
                <FormItem name='desc' label='集群说明'>
                  <Text />
                </FormItem>
              </>
            )}
          </>
        )}
      </Form>
      <Row justify='end' align='middle' style={{ marginTop: 20 }}>
        <SubTitle>请按照后续说明填写变量</SubTitle>
        <Button loading={submitting} onClick={() => form.submit()}>
          开始配置
        </Button>
      </Row>
    </Root>
  );
}

export default connect((state) => ({
  cache: state.cluster.quickDeploy.clusterInfo,
  quickDeployFlow: state.execFlow.quickDeployFlow,
  clusterType: state.cluster.quickDeploy.clusterType,
  stencilOptions: state.variable.stencilList?.map((item) => ({
    label: item.stencilName,
    value: item.id,
  })),
}))(ChoiceClusterStepK8s);

const Root = styled.div`
  margin-top: 20px;
`;

const SubTitle = styled.div`
  color: var(--td-text-color-secondary);
  margin-right: 16px;
`;
